import { Component } from '@angular/core';
import { TextInputComponent } from '../../../shared/components/text-input/text-input.component';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from '../../service/notification.service';
import { AuthService } from '../../service/auth-service';
import { CommonModule } from '@angular/common';
import { emailValidator } from '../../../shared/validators/common.validators';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-login',
  imports: [TextInputComponent, CommonModule, FormsModule, ReactiveFormsModule, RouterLink],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css'
})
export class LoginComponent {
  email: string = '';
  password: string = '';
  isPasswordVisible = false;
  signinForm!: FormGroup;
  loading:boolean = false;
  errorMessage = '';
  private isSubmitting = false;
  private readonly AUTO_LOGIN_PENDING_KEY = 'login.auto.pending';
  private readonly AUTO_LOGIN_EMAIL_KEY = 'login.auto.email';
  private readonly AUTO_LOGIN_PASSWORD_KEY = 'login.auto.password';
  private readonly AUTO_LOGIN_ATTEMPT_KEY = 'login.auto.attempt';
  private readonly AUTO_LOGIN_MAX_ATTEMPTS = 2;
  private loginWatchdogId: number | null = null;

  constructor(private formBuilder: FormBuilder, private router: Router,
    private authService: AuthService, private toastr: NotificationService,
    private dialog: MatDialog
  ) {
    this.signinForm = this.formBuilder.group({
      email: new FormControl('', emailValidator),
      password: new FormControl('', [Validators.required]),
    });

  }

  ngOnInit() {
    const pending = sessionStorage.getItem(this.AUTO_LOGIN_PENDING_KEY);
    if (pending === '1') {
      const attempt = parseInt(sessionStorage.getItem(this.AUTO_LOGIN_ATTEMPT_KEY) || '0', 10);
      if (Number.isFinite(attempt) && attempt > 0 && attempt <= this.AUTO_LOGIN_MAX_ATTEMPTS) {
        const email = sessionStorage.getItem(this.AUTO_LOGIN_EMAIL_KEY) || '';
        const password = sessionStorage.getItem(this.AUTO_LOGIN_PASSWORD_KEY) || '';
        this.signinForm.patchValue({ email, password }, { emitEvent: false });
        setTimeout(() => {
          this.signIn();
        }, 0);
      } else {
        this.clearAutoLoginState();
      }
    }
  }

  togglePasswordVisibility() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  signIn() {
    this.markFormGroupTouched(this.signinForm);
  
    if (this.loading || this.isSubmitting) {
      return;
    }

    if (this.signinForm.valid) {
      this.loading = true;
      this.isSubmitting = true;

      sessionStorage.setItem(this.AUTO_LOGIN_PENDING_KEY, '1');
      sessionStorage.setItem(this.AUTO_LOGIN_EMAIL_KEY, this.signinForm.value.email);
      sessionStorage.setItem(this.AUTO_LOGIN_PASSWORD_KEY, this.signinForm.value.password);

      if (this.loginWatchdogId !== null) {
        clearTimeout(this.loginWatchdogId);
      }
      this.loginWatchdogId = window.setTimeout(() => {
        if (this.loading) {
          this.refreshForAutoLogin();
        }
      }, 6000);
  
      const payload = {
        identifier: this.signinForm.value.email,
        password: this.signinForm.value.password,
      };
  
      this.authService.signIn(payload).pipe(
        finalize(() => {
          this.loading = false;
          this.isSubmitting = false;

          if (this.loginWatchdogId !== null) {
            clearTimeout(this.loginWatchdogId);
            this.loginWatchdogId = null;
          }
        })
      ).subscribe({
        next: (res) => {
          if (res?.jwt && res?.user) {
            // Save token & user
            this.authService.setToken(res.jwt);
            this.authService.setUser(res.user);

            this.clearAutoLoginState();
  
        
            this.toastr.success({message: 'Login successful'});
  

            const userType = res.user?.userType;
            setTimeout(() => {
              if (userType === 'NRI') {
                this.router.navigate(['/nri/dashboard']).then(success => {
                  if (!success) {
                    console.error('Route not found. Current config:', this.router.config);
                  }
                });
                // this.router.navigate(['nri', 'travel']); 
              } else if (userType === 'Service Provider') {
                this.router.navigate(['/service-provider/dashboard']);
              } 
              else if (userType === 'admin') {
                this.router.navigate(['/admin/properties']);
              }
              else {
                this.router.navigate(['/']);
              }
            }, 100); // <-- small delay
            
          } else {
            this.clearAutoLoginState();
            this.toastr.error({message: 'Invalid login response'});
          }
        },
        error: (error) => {
          if ([0, 408, 504].includes(error?.status)) {
            this.refreshForAutoLogin();
            return;
          }

          this.clearAutoLoginState();

          const message = [401, 403, 400, 408, 500, 504].includes(error.status)
            ? error?.error?.message || 'Invalid email or password'
            : (error?.error?.message || error.message || 'Something went wrong');
  
          this.errorMessage = message;
          this.toastr.error({message: this.errorMessage});
        }
      });
    }
  }

  private refreshForAutoLogin(): void {
    const currentAttempt = parseInt(sessionStorage.getItem(this.AUTO_LOGIN_ATTEMPT_KEY) || '0', 10);
    const nextAttempt = Number.isFinite(currentAttempt) ? currentAttempt + 1 : 1;

    if (nextAttempt > this.AUTO_LOGIN_MAX_ATTEMPTS) {
      this.clearAutoLoginState();
      this.toastr.error({ message: 'Login failed due to timeout. Please try again.' });
      return;
    }

    sessionStorage.setItem(this.AUTO_LOGIN_ATTEMPT_KEY, nextAttempt.toString());
    window.location.reload();
  }

  private clearAutoLoginState(): void {
    sessionStorage.removeItem(this.AUTO_LOGIN_PENDING_KEY);
    sessionStorage.removeItem(this.AUTO_LOGIN_EMAIL_KEY);
    sessionStorage.removeItem(this.AUTO_LOGIN_PASSWORD_KEY);
    sessionStorage.removeItem(this.AUTO_LOGIN_ATTEMPT_KEY);
  }
  
  getControl(fieldName: string): FormControl {
    return (this.signinForm.get(fieldName) as FormControl) || new FormControl();
  }


  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control instanceof FormControl) {
        control.markAsTouched();
        control.markAsDirty();
      }
    });
  }
}
